import React from 'react';
import { 
  Box, 
  Typography, 
  Stack,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  CircularProgress
} from '@mui/material';
import { ClaimContextView } from '../../../services/AppealsService';

interface ClaimContextPanelProps {
  encounterId: string;
  accountId?: string;
  claimContext?: ClaimContextView;
}

const ClaimContextPanel: React.FC<ClaimContextPanelProps> = ({ 
  encounterId, 
  accountId,
  claimContext 
}) => {
  // If we have pre-loaded context, display it
  if (claimContext) {
    return (
      <Box sx={{ fontSize: '0.875rem' }}>
        {/* Patient Info */}
        <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
          Patient
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {claimContext.patientName}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          DOB: {claimContext.patientDateOfBirth} • Member: {claimContext.patientMemberNumber}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Payer Info */}
        <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
          Payer
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {claimContext.payerName}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Plan: {claimContext.planName}
        </Typography>

        <Divider sx={{ my: 1.5 }} />

        {/* Claim Info */}
        <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
          Claim Details
        </Typography>
        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Claim #</Typography>
            <Typography variant="body2">{claimContext.claimNumber}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="textSecondary">DOS</Typography>
            <Typography variant="body2">{claimContext.dateOfService}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Charges</Typography>
            <Typography variant="body2">${claimContext.totalCharges?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Allowed</Typography>
            <Typography variant="body2">${claimContext.totalAllowed?.toFixed(2)}</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="textSecondary">Paid</Typography>
            <Typography variant="body2">${claimContext.totalPaid?.toFixed(2)}</Typography>
          </Box>
        </Stack>

        {/* Denial Info */}
        {claimContext.denialReasonCodes && claimContext.denialReasonCodes.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ color: '#ef4444', mb: 1 }}>
              Denial Reason(s)
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {claimContext.denialReasonCodes.map((code, idx) => (
                <Chip 
                  key={code}
                  label={`${code}: ${claimContext.denialReasonDescriptions?.[idx] || ''}`}
                  size="small"
                  color="error"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Box>
            {claimContext.appealDeadline && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                Appeal Deadline: {claimContext.appealDeadline}
              </Typography>
            )}
          </>
        )}

        {/* Diagnosis Codes */}
        {claimContext.diagnosisCodes && claimContext.diagnosisCodes.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
              Diagnosis Codes
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {claimContext.diagnosisCodes.slice(0, 5).map((dx) => (
                <Chip 
                  key={dx.code}
                  label={dx.code}
                  size="small"
                  variant="outlined"
                  title={dx.description}
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {claimContext.diagnosisCodes.length > 5 && (
                <Chip 
                  label={`+${claimContext.diagnosisCodes.length - 5} more`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </>
        )}

        {/* Procedure Codes */}
        {claimContext.procedureCodes && claimContext.procedureCodes.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" sx={{ color: '#64748b', mb: 1 }}>
              Procedure Codes
            </Typography>
            <List dense sx={{ py: 0 }}>
              {claimContext.procedureCodes.slice(0, 3).map((cpt) => (
                <ListItem key={cpt.code} sx={{ px: 0, py: 0.25 }}>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        {cpt.code}{cpt.modifier ? `-${cpt.modifier}` : ''}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        ${cpt.chargeAmount?.toFixed(2)} → ${cpt.paidAmount?.toFixed(2)}
                        {cpt.deniedReason && ` (${cpt.deniedReason})`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
              {claimContext.procedureCodes.length > 3 && (
                <Typography variant="caption" color="textSecondary">
                  +{claimContext.procedureCodes.length - 3} more procedures
                </Typography>
              )}
            </List>
          </>
        )}
      </Box>
    );
  }

  // Fallback: Show loading or minimal info
  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <CircularProgress size={24} sx={{ mb: 1 }} />
      <Typography variant="body2" color="textSecondary">
        Loading claim context...
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
        Encounter: {encounterId}
        {accountId && ` • Account: ${accountId}`}
      </Typography>
    </Box>
  );
};

export default ClaimContextPanel;
